import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api, { fmtErr, fmtMoney, toastErr } from "../../api";
import { toast } from "sonner";
import { TermsPanel } from "./QuoteTermsPanel";
import { QuoteActions } from "./QuoteActions";
import { QuoteOneTime } from "./QuoteOneTime";
import { MarkAcceptedPanel, ConvertPanel, ReviseButton, ConvertedInfo, WithdrawButton } from "./QuoteAcceptance";

const BILLING_MODELS = [
  ["season_lump_sum_on_acceptance", "One invoice on acceptance"],
  ["per_visit", "Per visit"],
  ["monthly", "Monthly"],
  ["seasonal", "Seasonal instalments"],
];
const STEPS = ["Customer", "Site & season", "Pricing options", "Review"];
const inp = "field-input mt-1 w-full";
const L = ({ label, children, testid }) => (
  <label className="block text-xs font-bold" data-testid={testid}>{label}{children}</label>
);

const suggestVisits = (freq, start, end) => {
  const days = (new Date(end) - new Date(start)) / 864e5;
  if (!days || days <= 0) return "";
  const weeks = days / 7;
  if (freq === "weekly") return Math.round(weeks);
  if (freq === "bi-weekly") return Math.ceil(weeks / 2);
  if (freq === "every_3_weeks") return Math.ceil(weeks / 3);
  if (freq === "monthly") return Math.max(1, Math.round(days / 30.4));
  return "";
};

export default function QuoteNew() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [q, setQ] = useState(null);
  const [form, setForm] = useState({
    kind: sp.get("lead") ? "lead" : sp.get("owner") ? "owner" : "lead",
    lead_id: sp.get("lead") || "", owner_id: sp.get("owner") || "", property_id: sp.get("property") || "",
    new_lead: { name: "", company: "", email: "", phone: "", address: "",
      billing_name: "", billing_company: "", billing_email: "", billing_cc_email: "", billing_phone: "",
      billing_address: "", billing_same_as_service: true, site_contact_name: "", site_contact_phone: "" },
    contact_name: "", company_name: "", email: "", phone: "", service_address: "",
    template_id: "", service_area_value: "", service_area_unit: "acres", site_notes: "",
    season_year: new Date().getFullYear() + 1, frequency: "weekly", visit_count: 26,
    season_start_text: "", season_end_text: "", start_date: "", end_date: "",
    options: [{ label: "A", description: "", pricing_basis: "per_visit", per_visit: "", season_total: "",
                extra_visit_rate: "", note: "", clauses: [], frequency: "weekly", visits: 26 }],
    billing_model: "season_lump_sum_on_acceptance", payment_terms_days: "", valid_until: "",
    acceptance_method: "link",
  });
  const [leads, setLeads] = useState([]);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [termsLib, setTermsLib] = useState({ standard: [], custom: [] });
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  // pricing helper (§13.10)
  const [helperOpen, setHelperOpen] = useState(false);
  const [rateAcre, setRateAcre] = useState("");
  const [saveRateDefault, setSaveRateDefault] = useState(false);
  const [helperArea, setHelperArea] = useState("");
  const [discount, setDiscount] = useState("");
  // savings note (§13.11)
  const [note, setNote] = useState("");
  const [noteShow, setNoteShow] = useState(false);
  const [noteTouched, setNoteTouched] = useState(false);
  const [priceSig, setPriceSig] = useState("");

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(Array.isArray(r.data) ? r.data : r.data.leads || [])).catch(() => {});
    api.get("/owners").then((r) => setOwners(Array.isArray(r.data) ? r.data : r.data.owners || [])).catch(() => {});
    api.get("/quote-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/quote-terms").then((r) => setTermsLib(r.data)).catch(() => {});
    api.get("/settings").then((r) => setRateAcre(r.data.quote_rate_per_acre || "")).catch(() => {});
    api.get("/properties").then((r) => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/quotes/${id}`).then((r) => {
      const d = r.data;
      setQ(d);
      setNote(d.comparison_note || "");
      setNoteShow(!!d.comparison_note_show);
      setPriceSig(JSON.stringify(d.options || []));
      setForm((f) => ({ ...f, ...d, kind: d.owner_id ? "owner" : "lead",
        new_lead: f.new_lead,
        options: (d.options || []).length ? d.options : f.options }));
    }).catch((e) => toastErr(e));
  }, [id]);

  const payload = () => ({
    lead_id: form.kind === "lead" && form.lead_id ? form.lead_id : null,
    owner_id: form.kind === "owner" && form.owner_id ? form.owner_id : null,
    property_id: form.property_id || null,
    new_lead: !q && form.kind === "new" ? form.new_lead : null,
    contact_name: form.contact_name || null, company_name: form.company_name || null,
    email: form.email || null, phone: form.phone || null, service_address: form.service_address || null,
    template_id: form.template_id || null,
    service_area_value: form.service_area_value === "" ? null : Number(form.service_area_value),
    service_area_unit: form.service_area_unit, site_notes: form.site_notes,
    season_year: Number(form.season_year), frequency: form.options[0]?.frequency || "weekly",
    visit_count: form.options[0]?.visits ? Number(form.options[0].visits) : null,
    season_start_text: form.season_start_text, season_end_text: form.season_end_text,
    start_date: form.start_date || null, end_date: form.end_date || null,
    options: form.options.map((o) => ({ label: o.label, description: o.description || "",
      pricing_basis: o.pricing_basis, note: o.note || "", clauses: o.clauses || [],
      frequency: o.frequency || "weekly",
      visits: o.visits === "" || o.visits == null ? null : Number(o.visits),
      per_visit: o.pricing_basis === "per_visit" && o.per_visit !== "" ? Number(o.per_visit) : null,
      season_total: o.pricing_basis === "season_total" && o.season_total !== "" && o.season_total != null ? Number(o.season_total) : null,
      extra_visit_rate: o.extra_visit_rate === "" || o.extra_visit_rate == null ? null : Number(o.extra_visit_rate) })),
    comparison_note: note, comparison_note_show: noteShow,
    billing_model: form.billing_model,
    payment_terms_days: form.payment_terms_days === "" ? null : Number(form.payment_terms_days),
    valid_until: form.valid_until || null,
    acceptance_method: form.acceptance_method,
  });

  const locked = q && q.status !== "draft";
  const save = async (advance = false) => {
    if (locked) { if (advance) setStep((s) => Math.min(s + 1, 3)); return; } // sent quotes don't save — navigate only
    setBusy(true);
    try {
      if (!q) {
        const r = await api.post("/quotes", payload());
        setQ(r.data);
        window.history.replaceState(null, "", `/quotes/${r.data.id}`);
        toast.success(`Draft ${r.data.quote_number} saved`);
      } else {
        const r = await api.patch(`/quotes/${q.id}`, payload());
        setQ(r.data);
      }
      if (advance) setStep((s) => Math.min(s + 1, 3));
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  const applyTemplate = async (tid) => {
    if (q && tid !== (q.template_id || "")
        && !window.confirm("This replaces the scope and terms text on this quote")) return;
    setForm((f) => ({ ...f, template_id: tid }));
    if (q) {
      try {
        const r = await api.patch(`/quotes/${q.id}`, { template_id: tid });
        setQ(r.data);
        toast.success("Template applied to this quote");
      } catch (e) { toastErr(e); }
    } else {
      const t = templates.find((x) => x.id === tid);
      if (t) setForm((f) => ({ ...f, template_id: tid,
        visit_count: t.defaults?.visit_count ?? f.visit_count,
        frequency: t.defaults?.frequency ?? f.frequency,
        payment_terms_days: t.defaults?.payment_terms_days ?? f.payment_terms_days,
        billing_model: t.defaults?.billing_model ?? f.billing_model }));
    }
  };

  const patchQuote = async (body) => {
    try { const r = await api.patch(`/quotes/${q.id}`, body); setQ(r.data); return r.data; }
    catch (e) { toastErr(e); return null; }
  };

  const validate = async () => {
    if (!q) return;
    const r = await api.post(`/quotes/${q.id}/validate`).catch((e) => { toastErr(e); return null; });
    if (r) setWarnings(r.data.warnings);
  };
  useEffect(() => { if (step === 3 && q) validate(); }, [step, q?.id]);

  // §13.10 pricing helper
  const area = Number(helperArea || form.service_area_value) || 0;
  const rate = Number(rateAcre) || 0;
  const suggestedPV = area && rate ? Math.round(area * rate) : null;
  const visits = Number(form.visit_count) || 0;
  const seasonFull = suggestedPV && visits ? suggestedPV * visits : null;
  const disc = Number(discount) || 0;
  const seasonDisc = seasonFull && disc ? Math.round(seasonFull * (1 - disc / 100)) : null;
  const useForA = async () => {
    if (!suggestedPV) return;
    setForm((f) => {
      const ops = [...f.options];
      ops[0] = { ...ops[0], pricing_basis: "per_visit", per_visit: String(suggestedPV), season_total: "" };
      return { ...f, options: ops };
    });
    toast.success(`Option A set to ${fmtMoney(suggestedPV)} per visit — adjust freely`);
    if (saveRateDefault && rate) {
      await api.patch("/settings", { quote_rate_per_acre: rate }).catch((e) => toastErr(e));
    }
  };

  // §13.11 savings note — re-suggest only when prices change and the operator hasn't edited
  const suggestion = q?.suggested_comparison_note || "";
  const sig = JSON.stringify(form.options.map((o) => [o.pricing_basis, o.per_visit, o.season_total]));
  useEffect(() => {
    if (!noteTouched && sig !== priceSig && suggestion) { setNote(suggestion); setPriceSig(sig); }
  }, [sig, suggestion]);

  // §13.7 terms on this quote
  const terms = q?.terms || [];

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // §14.2: new quotes (and lead/owner "Send quote") first ask one-time vs seasonal
  if (!id && !sp.get("type")) {
    const carry = [sp.get("lead") && `lead=${sp.get("lead")}`, sp.get("owner") && `owner=${sp.get("owner")}`,
                   sp.get("property") && `property=${sp.get("property")}`].filter(Boolean).join("&");
    return (
      <Layout title="New quote">
        <p className="mb-4 text-sm text-muted-foreground">What are you quoting?</p>
        <div className="space-y-2" data-testid="quote-type-chooser">
          <Link to={`/quotes/new?type=one_time${carry ? `&${carry}` : ""}`} data-testid="quote-type-one-time"
            className="card-flat block p-5 transition-colors hover:bg-muted/50">
            <p className="font-heading text-lg font-bold">One-time job</p>
            <p className="text-sm text-muted-foreground">A single visit — leaf cleanup, spring opening, garden bed cleanup. One screen, priced by the job.</p>
          </Link>
          <Link to={`/quotes/new?type=seasonal${carry ? `&${carry}` : ""}`} data-testid="quote-type-seasonal"
            className="card-flat block p-5 transition-colors hover:bg-muted/50">
            <p className="font-heading text-lg font-bold">Seasonal service</p>
            <p className="text-sm text-muted-foreground">Recurring visits across a season — weekly mowing, snow coverage. Four steps with pricing options.</p>
          </Link>
        </div>
      </Layout>
    );
  }
  if (sp.get("type") === "one_time" || q?.quote_type === "one_time") {
    return <QuoteOneTime editId={id} />;
  }

  return (
    <Layout title={q ? `Quote ${q.quote_number}` : "New service quote"}>
      <div className="mb-4 flex gap-1" data-testid="wizard-steps">
        {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded ${i <= step ? "bg-primary" : "bg-muted"}`} />)}
      </div>
      <p className="mb-3 text-sm font-bold" data-testid="wizard-step-label">{step + 1}. {STEPS[step]}</p>

      {step === 0 && (
        <div className="space-y-3" data-testid="step-customer">
          <div className="flex gap-2">
            {[["lead", "Existing lead"], ["owner", "Existing owner"], ["new", "New lead"]].map(([k, lbl]) => (
              <button key={k} data-testid={`kind-${k}`} onClick={() => setForm((f) => ({ ...f, kind: k }))}
                className={`flex-1 rounded-md border-2 px-2 py-2 text-xs font-bold ${form.kind === k ? "border-primary bg-primary/10" : "border-border"}`}>{lbl}</button>
            ))}
          </div>
          {form.kind === "lead" && (
            <L label="Lead" testid="lbl-lead"><select data-testid="pick-lead" className={inp} value={form.lead_id}
              onChange={(e) => setForm((f) => ({ ...f, lead_id: e.target.value }))}>
              <option value="">Choose a lead…</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.company ? ` — ${l.company}` : ""}</option>)}
            </select></L>
          )}
          {form.kind === "owner" && (
            <>
              <L label="Owner" testid="lbl-owner"><select data-testid="pick-owner" className={inp} value={form.owner_id}
                onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}>
                <option value="">Choose an owner…</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></L>
              <L label="Property (optional)" testid="lbl-property"><select data-testid="pick-property" className={inp} value={form.property_id}
                onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))}>
                <option value="">Choose a property…</option>
                {properties.filter((p) => !form.owner_id || p.owner_id === form.owner_id)
                  .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></L>
            </>
          )}
          {form.kind === "new" && (
            <div className="space-y-2" data-testid="new-lead-form">
              {[["name", "Name"], ["company", "Company (optional)"], ["email", "Email"], ["phone", "Phone"], ["address", "Service address"]].map(([k, lbl]) => (
                <L key={k} label={lbl} testid={`lbl-new-${k}`}>
                  <input data-testid={`new-lead-${k}`} className={inp} value={form.new_lead[k]}
                    onChange={(e) => setForm((f) => ({ ...f, new_lead: { ...f.new_lead, [k]: e.target.value } }))} />
                </L>
              ))}
              <p className="pt-1 text-xs font-bold text-muted-foreground">Billing contact (if different)</p>
              {[["billing_name", "Billing contact name"], ["billing_company", "Company / org (optional)"], ["billing_email", "Billing email"], ["billing_cc_email", "CC email (optional)"], ["billing_phone", "Billing phone"]].map(([k, lbl]) => (
                <L key={k} label={lbl} testid={`lbl-new-${k}`}>
                  <input data-testid={`new-lead-${k}`} className={inp} value={form.new_lead[k]}
                    onChange={(e) => setForm((f) => ({ ...f, new_lead: { ...f.new_lead, [k]: e.target.value } }))} />
                </L>
              ))}
              <label className="flex items-center gap-2 text-xs font-semibold">
                <input type="checkbox" data-testid="new-lead-billing-same" checked={form.new_lead.billing_same_as_service}
                  onChange={(e) => setForm((f) => ({ ...f, new_lead: { ...f.new_lead, billing_same_as_service: e.target.checked } }))} />
                Billing address same as service address
              </label>
              {!form.new_lead.billing_same_as_service && (
                <L label="Billing address" testid="lbl-new-billing-address">
                  <input data-testid="new-lead-billing_address" className={inp} value={form.new_lead.billing_address}
                    onChange={(e) => setForm((f) => ({ ...f, new_lead: { ...f.new_lead, billing_address: e.target.value } }))} />
                </L>
              )}
              <p className="pt-1 text-xs font-bold text-muted-foreground">Site contact at the property (optional)</p>
              <div className="flex gap-2">
                <input data-testid="new-lead-site_contact_name" className={inp} placeholder="Name" value={form.new_lead.site_contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, new_lead: { ...f.new_lead, site_contact_name: e.target.value } }))} />
                <input data-testid="new-lead-site_contact_phone" className={inp} placeholder="Phone" value={form.new_lead.site_contact_phone}
                  onChange={(e) => setForm((f) => ({ ...f, new_lead: { ...f.new_lead, site_contact_phone: e.target.value } }))} />
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Recipient details below pre-fill from the lead/owner and stay editable.</p>
          <L label="Contact name" testid="lbl-contact-name"><input data-testid="contact-name" className={inp} value={form.contact_name} onChange={setF("contact_name")} /></L>
          <L label="Company (optional)" testid="lbl-company"><input data-testid="company-name" className={inp} value={form.company_name} onChange={setF("company_name")} /></L>
          <L label="Email" testid="lbl-email"><input data-testid="contact-email" className={inp} value={form.email} onChange={setF("email")} /></L>
          <L label="Phone" testid="lbl-phone"><input data-testid="contact-phone" className={inp} value={form.phone} onChange={setF("phone")} /></L>
          <L label="Service address" testid="lbl-address"><input data-testid="service-address" className={inp} value={form.service_address} onChange={setF("service_address")} /></L>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-3" data-testid="step-site">
          <L label="Template" testid="lbl-template">
            <select data-testid="pick-template" className={inp} value={form.template_id} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">No template</option>
              {templates.filter((t) => (t.quote_type || "seasonal") === "seasonal").map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</option>)}
            </select>
          </L>
          <p className="text-xs text-muted-foreground" data-testid="template-help">
            A template fills in the scope of work, what's included each visit, the season and billing wording,
            exclusions and default terms. Change it any time before sending.
            <Link to="/settings/quotes#set-quote-templates" data-testid="manage-templates" className="ml-1 font-bold text-primary">Manage templates</Link>
          </p>
          <div className="flex gap-2">
            <L label="Area" testid="lbl-area"><input data-testid="area-value" type="number" className={inp} value={form.service_area_value}
              onChange={(e) => { setF("service_area_value")(e); setHelperArea(e.target.value); }} /></L>
            <L label="Unit" testid="lbl-unit"><select data-testid="area-unit" className={inp} value={form.service_area_unit} onChange={setF("service_area_unit")}>
              <option value="acres">acres</option><option value="sq ft">sq ft</option>
            </select></L>
          </div>
          <L label="Site notes (optional)" testid="lbl-site-notes"><input data-testid="site-notes" className={inp} value={form.site_notes} onChange={setF("site_notes")} /></L>
          <div className="flex gap-2">
            <L label="Season year" testid="lbl-year"><input data-testid="season-year" type="number" className={inp} value={form.season_year} onChange={setF("season_year")} /></L>
          </div>
          <p className="text-xs text-muted-foreground">Visit frequency and counts are set per option in the next step.</p>
          <L label="Season start (wording on quote)" testid="lbl-season-start"><input data-testid="season-start-text" className={inp} value={form.season_start_text} onChange={setF("season_start_text")} /></L>
          <L label="Season end (wording on quote)" testid="lbl-season-end"><input data-testid="season-end-text" className={inp} value={form.season_end_text} onChange={setF("season_end_text")} /></L>
          <div className="flex gap-2">
            <L label="Start date (used at conversion)" testid="lbl-start-date"><input data-testid="start-date" type="date" className={inp} value={form.start_date} onChange={setF("start_date")} /></L>
            <L label="End date" testid="lbl-end-date"><input data-testid="end-date" type="date" className={inp} value={form.end_date} onChange={setF("end_date")} /></L>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="step-options">
          <div className="card-flat p-3" data-testid="pricing-helper-wrap">
            <button data-testid="pricing-helper-toggle" onClick={() => setHelperOpen(!helperOpen)} className="text-xs font-bold text-primary">
              {helperOpen ? "▾" : "▸"} Pricing helper (optional)</button>
            {helperOpen && (
              <div className="mt-2 space-y-2" data-testid="pricing-helper">
                <div className="flex gap-2">
                  <L label="Rate per acre" testid="lbl-rate-acre">
                    <input data-testid="rate-acre" type="number" className={inp} value={rateAcre} onChange={(e) => setRateAcre(e.target.value)} /></L>
                  <L label="Area" testid="lbl-helper-area">
                    <input data-testid="helper-area" type="number" className={inp} value={helperArea || form.service_area_value} onChange={(e) => setHelperArea(e.target.value)} /></L>
                  <L label="Seasonal discount %" testid="lbl-discount">
                    <input data-testid="helper-discount" type="number" className={inp} value={discount} onChange={(e) => setDiscount(e.target.value)} /></L>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" data-testid="rate-save-default" checked={saveRateDefault} onChange={(e) => setSaveRateDefault(e.target.checked)} />
                  Save as my default
                </label>
                {suggestedPV && (
                  <p className="text-sm" data-testid="helper-suggestion">
                    Suggested per visit: <strong>{fmtMoney(suggestedPV)}</strong> ({area} acres × {fmtMoney(rate)})
                    {seasonDisc ? <span data-testid="helper-discount-line"><br />Season at discount: <strong>{fmtMoney(seasonDisc)}</strong> (vs {fmtMoney(seasonFull)} at the per-visit rate)</span> : null}
                  </p>
                )}
                <button data-testid="helper-use-a" onClick={useForA} disabled={!suggestedPV}
                  className="rounded-md border-2 border-primary px-3 py-2 text-xs font-bold text-primary disabled:opacity-40">Use for Option A</button>
                <p className="text-xs text-muted-foreground">A suggestion only — nothing changes until you tap the button.</p>
              </div>
            )}
          </div>
          {form.options.map((o, i) => (
            <div key={o.label} className="card-flat space-y-2 p-4" data-testid={`option-${o.label}`}>
              <div className="flex items-center justify-between">
                <p className="font-bold">Option {o.label}</p>
                {form.options.length > 1 && (
                  <button data-testid={`option-${o.label}-remove`} className="text-xs font-bold text-red-600"
                    onClick={() => setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }))}>Remove</button>
                )}
              </div>
              <L label="Description" testid={`lbl-opt-${o.label}-desc`}>
                <input className={inp} value={o.description || ""}
                  onChange={(e) => setForm((f) => { const ops = [...f.options]; ops[i] = { ...o, description: e.target.value }; return { ...f, options: ops }; })} /></L>
              <div className="flex gap-2">
                <L label="Pricing basis" testid={`lbl-opt-${o.label}-basis`}>
                  <select className={inp} value={o.pricing_basis}
                    onChange={(e) => setForm((f) => { const ops = [...f.options]; ops[i] = { ...o, pricing_basis: e.target.value }; return { ...f, options: ops }; })}>
                    <option value="per_visit">per-visit price</option>
                    <option value="season_total">season total</option>
                  </select></L>
                <L label={o.pricing_basis === "per_visit" ? "$ per visit" : "$ season total"} testid={`lbl-opt-${o.label}-figure`}>
                  <input data-testid={`option-${o.label}-figure`} type="number" className={inp}
                    value={o.pricing_basis === "per_visit" ? o.per_visit : (o.season_total ?? "")}
                    onChange={(e) => setForm((f) => { const ops = [...f.options];
                      ops[i] = { ...o, [o.pricing_basis === "per_visit" ? "per_visit" : "season_total"]: e.target.value };
                      return { ...f, options: ops }; })} /></L>
              </div>
              <div className="flex gap-2">
                <L label="Frequency" testid={`lbl-opt-${o.label}-freq`}>
                  <select data-testid={`option-${o.label}-frequency`} className={inp} value={o.frequency || "weekly"}
                    onChange={(e) => setForm((f) => { const ops = [...f.options];
                      const freq = e.target.value;
                      ops[i] = { ...o, frequency: freq, visits: o.visits || suggestVisits(freq, f.start_date, f.end_date) };
                      return { ...f, options: ops }; })}>
                    <option value="weekly">weekly</option><option value="bi-weekly">bi-weekly</option>
                    <option value="every_3_weeks">every 3 weeks</option><option value="monthly">monthly</option>
                    <option value="custom">custom</option>
                  </select></L>
                <L label="Visits" testid={`lbl-opt-${o.label}-visits`}>
                  <input data-testid={`option-${o.label}-visits`} type="number" className={inp} value={o.visits ?? ""}
                    onChange={(e) => setForm((f) => { const ops = [...f.options]; ops[i] = { ...o, visits: e.target.value }; return { ...f, options: ops }; })} /></L>
              </div>
              <L label="Extra visit rate ($)" testid={`lbl-opt-${o.label}-extra`}>
                <input data-testid={`option-${o.label}-extra-rate`} type="number" className={inp} value={o.extra_visit_rate ?? ""}
                  onChange={(e) => setForm((f) => { const ops = [...f.options]; ops[i] = { ...o, extra_visit_rate: e.target.value }; return { ...f, options: ops }; })} /></L>
            </div>
          ))}
          {form.options.length < 3 && (
            <button data-testid="add-option" className="text-sm font-bold text-primary"
              onClick={() => setForm((f) => ({ ...f, options: [...f.options,
                { label: "ABC"[f.options.length], description: "", pricing_basis: "per_visit", per_visit: "",
                  season_total: "", extra_visit_rate: "", note: "", clauses: [],
                  frequency: f.options[0]?.frequency || "weekly", visits: f.options[0]?.visits || "" }] }))}>
              + Add option {"ABC"[form.options.length]}
            </button>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="step-review">
          {terms.length > 0 && (
            <TermsPanel terms={terms} termsLib={termsLib} quoteType="seasonal"
              onChange={(t) => patchQuote({ terms: t })} onLibChange={setTermsLib} />
          )}
          <div className="card-flat space-y-2 p-4" data-testid="savings-note-block">
            <div className="flex items-center justify-between">
              <L label="Savings note (optional)" testid="lbl-savings-note"><span /></L>
              <label className="flex items-center gap-2 text-xs font-semibold">
                <input type="checkbox" data-testid="savings-show" checked={noteShow}
                  onChange={(e) => { setNoteShow(e.target.checked); if (q) patchQuote({ comparison_note_show: e.target.checked }); }} />
                Show on quote
              </label>
            </div>
            <textarea data-testid="comparison-note" className="field-input w-full" rows={2} value={note}
              onChange={(e) => { setNote(e.target.value); setNoteTouched(true); }}
              onBlur={() => { if (q) patchQuote({ comparison_note: note }); }} />
            {suggestion && suggestion !== note && (
              <button data-testid="savings-resuggest" className="text-xs font-bold text-primary"
                onClick={() => { setNote(suggestion); setNoteTouched(false); if (q) patchQuote({ comparison_note: suggestion }); }}>
                Re-suggest: {suggestion}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <L label="Valid until" testid="lbl-valid"><input data-testid="valid-until" type="date" className={inp} value={form.valid_until || q?.valid_until || ""} onChange={setF("valid_until")} /></L>
            <L label="Payment terms (days)" testid="lbl-terms-days"><input data-testid="payment-terms" type="number" className={inp} value={form.payment_terms_days || q?.payment_terms_days || ""} onChange={setF("payment_terms_days")} /></L>
          </div>
          <L label="Billing after acceptance" testid="lbl-billing-model">
            <select data-testid="billing-model" className={inp} value={form.billing_model} onChange={setF("billing_model")}>
              {BILLING_MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></L>
          <L label="Acceptance method" testid="lbl-acceptance">
            <select data-testid="acceptance-method" className={inp} value={form.acceptance_method} onChange={setF("acceptance_method")}>
              <option value="link">Acceptance via link</option>
              <option value="paper">Paper (PDF only, no link)</option>
            </select></L>
          {warnings.length > 0 && (
            <div className="card-flat border-2 border-amber-400 p-3" data-testid="merge-warnings">
              <p className="text-sm font-bold text-amber-700">Fix before sending</p>
              {warnings.map((w) => <p key={w} className="text-xs text-amber-700">• {w}</p>)}
            </div>
          )}
          {q && (
            <a data-testid="preview-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/pdf`}
              target="_blank" rel="noreferrer"
              className="block rounded-md border-2 border-primary py-3 text-center text-sm font-bold text-primary">Preview PDF</a>
          )}
          {q && <QuoteActions q={q} onChanged={setQ} />}
          {q && ["sent", "viewed", "declined", "expired"].includes(q.status) && (
            <div className="flex gap-2">
              <ReviseButton q={q} />
              <WithdrawButton q={q} />
            </div>
          )}
          {q && q.status === "superseded" && q.revisions?.length > 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="quote-superseded-banner">
              This revision was superseded.{" "}
              <Link className="font-bold text-primary underline" data-testid="quote-latest-rev"
                to={`/quotes/${[...q.revisions].sort((a, b) => b.revision - a.revision)[0].id}`}>Open the latest revision →</Link>
            </p>
          )}
          {q && q.revisions?.length > 0 && (
            <div className="card-flat p-3" data-testid="revision-history">
              <p className="label-caps mb-1">Revision history</p>
              {q.revisions.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-0.5 text-xs" data-testid={`revision-row-${r.revision}`}>
                  <span>Rev {r.revision} — <span className="capitalize">{r.status}</span> · {(r.created_at || "").slice(0, 10)}</span>
                  <a className="font-bold text-primary" data-testid={`revision-pdf-${r.revision}`}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${r.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>
                </div>
              ))}
            </div>
          )}
          {q && q.quote_type !== "one_time" && ["draft", "sent", "viewed"].includes(q.status) && (
            <MarkAcceptedPanel q={q} onChanged={setQ} />
          )}
          {q && q.quote_type !== "one_time" && q.status === "accepted" && (
            <div className="space-y-2" data-testid="quote-accepted-block">
              <p className="text-xs font-bold text-primary" data-testid="quote-accepted-line">
                Accepted{q.accepted_name ? ` by ${q.accepted_name}` : ""}{q.accepted_option ? ` — Option ${q.accepted_option}` : ""}{q.po_number ? ` · PO ${q.po_number}` : ""}
              </p>
              {q.converted_agreement_id
                ? <ConvertedInfo q={q} />
                : <ConvertPanel q={q} onChanged={async () => setQ((await api.get(`/quotes/${q.id}`)).data)} />}
              <ReviseButton q={q} />
            </div>
          )}
          {q && <p className="text-xs text-muted-foreground" data-testid="quote-number-line">Number: {q.quote_number} (editable until first send)</p>}
        </div>
      )}

      <div className="mt-6 flex gap-2">
        {step > 0 && (
          <button data-testid="wizard-back" onClick={() => setStep((s) => s - 1)}
            className="h-12 flex-1 rounded-md border-2 border-border text-sm font-bold">Back</button>
        )}
        {!locked && (
          <button data-testid="wizard-next" disabled={busy} onClick={() => save(true)}
            className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">
            {busy ? "Saving…" : step === 3 ? "Save draft" : "Save & continue"}
          </button>
        )}
        {locked && step < 3 && (
          <button data-testid="wizard-next" onClick={() => save(true)}
            className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground">
            Continue
          </button>
        )}
      </div>
    </Layout>
  );
}
